import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { Box } from '../box/Box';
import type { NameSizingScale } from '../component.types';
import { Form } from '../form/Form';
import { Text } from '../text/Text';
import styles from './Settings.module.scss';

const cvaMultipleChoice = cva(styles.containerMultipleChoice, {
  variants: {
    variant: {
      default: styles['variant-default'],
    },
  },
  defaultVariants: { variant: 'default' },
});

type Option = {
  value: string | number | boolean;
  type?: 'radio' | 'checkbox';
  label: string;
  helpText?: string;
};

type NativeInputProps = Omit<React.HTMLAttributes<HTMLInputElement>, 'name'>;
// React.HTMLAttributes<HTMLInputElement> &
//  React.InputHTMLAttributes<HTMLInputElement>;
export type MultipleChoiceProps<T extends FieldValues> = UseControllerProps<T> &
  NativeInputProps &
  VariantProps<typeof cvaMultipleChoice> & {
    /** @link https://www.radix-ui.com/primitives/docs/utilities/slot */
    asChild?: boolean;
    /** Pre-styled title, or supply your own via `children` */
    title?: string;
    /** Pre-styled description, or supply your own via `children` */
    description?: string | React.ReactNode;
    size?: NameSizingScale;
    /** Simple radio/checkbox options */
    options: Option[];
    /** When default value is chosen, should the other options be hidden? */
    defaultValueHidesOptions?: boolean;
    /** What is the "global default" for this setting? E.g, global memory preferences vs per-tool preferences */
    defaultOption?: Omit<Option, 'label'> & {
      /** When default value is unselected, which option should be selected? */
      fallbackToValue: string;
      /** Defaults to "Don't customize" if not specified  */
      label?: string;
    };
    /** In case you need to attach a ref to the container (as TS generic + forwardRef are a giant PITA it seems) */
    forwardedRef?: React.Ref<HTMLInputElement>;
  };

export function MultipleChoice<T extends FieldValues>(props: MultipleChoiceProps<T>) {
  const {
    variant,
    asChild,
    size = 'small',
    defaultOption,
    children,
    name,
    control,
    options,
    defaultValueHidesOptions = false,
    title,
    description,
    forwardedRef,
    ...rest
  } = props;
  const Component = asChild ? Slot : Box.Column;

  const {
    field,
    formState: { errors },
  } = useController<T>({
    name,
    control,
    rules: { required: true },
  });

  const hideOptions = defaultOption?.value === field.value && !!defaultValueHidesOptions;
  const defaultFallbackOption = options.find(o => o.value === defaultOption?.fallbackToValue);

  return (
    <Component
      gap="large"
      {...rest}
      ref={forwardedRef}
      className={cvaMultipleChoice({
        variant,
        className: rest.className,
      })}
    >
      <React.Fragment>
        {(!!title || !!description) && (
          <Box.Column gap="small">
            {!!title && <Text.Label size="large">{title}</Text.Label>}
            {!!description && (
              <Text.Paragraph color="subtle" size="medium">
                {description}
              </Text.Paragraph>
            )}
          </Box.Column>
        )}
        {!!defaultOption && (
          <Box.Row gap="medium" style={{ paddingBottom: 4 * 2 }}>
            <Form.Input
              id={`use-default-${field.name}-${field.value}`}
              data-testid={`use-default-${field.name}-${field.value}`}
              {...field}
              value={`${defaultOption.value}`}
              type={defaultOption.type || 'checkbox'}
              checked={isChecked(defaultOption.value, field.value)}
              onChange={() => {
                const value =
                  defaultOption.value === field.value
                    ? defaultOption.fallbackToValue || options[0].value
                    : defaultOption.value;
                field.onChange(value);
              }}
            />
            <Form.Label htmlFor={`use-default-${field.name}-${field.value}`} size={size}>
              {defaultOption.label || "Don't customize"}
              {!!defaultFallbackOption && isChecked(defaultOption.value, field.value) && (
                <Text.Label size="xsmall" color="subtle">
                  &nbsp; (use "{defaultFallbackOption.label}")
                </Text.Label>
              )}
            </Form.Label>
          </Box.Row>
        )}
        {!hideOptions &&
          options.map(option => {
            const id = `mc-${field.name}-${option.value}-${option.label}`;

            const checked = isChecked(option.value, field.value);
            const color = defaultOption?.value === field.value ? 'subtle' : undefined;
            return (
              <Box.Row gap="medium" key={id}>
                <Form.Input
                  id={id}
                  data-testid={id}
                  type={option.type || 'radio'}
                  {...field}
                  value={`${option.value}`}
                  checked={checked}
                />
                <Form.Label htmlFor={id} size={size} color={color}>
                  {option.label}
                  {defaultOption?.fallbackToValue === option.value && (
                    <Text.Label size="xsmall" color="subtle">
                      &nbsp;(default)
                    </Text.Label>
                  )}
                  {!!option.helpText && (
                    <Text.Label size="xsmall" color="subtle">
                      &nbsp;{option.helpText}
                    </Text.Label>
                  )}
                </Form.Label>
              </Box.Row>
            );
          })}
        {typeof errors?.[field.name]?.message === 'string' && (
          <Form.Error>
            <>{errors?.[field.name]?.message as string}</>
          </Form.Error>
        )}
        {children}
        {/* {JSON.stringify({ options, field, fieldState }, null, 2)} */}
      </React.Fragment>
    </Component>
  );
}

MultipleChoice.displayName = 'Settings.FormControls.MultipleChoice';

const isChecked = (optionValue: string | number | boolean, fieldValue: string) => {
  return (
    optionValue === fieldValue || +fieldValue === optionValue || Boolean(fieldValue) === optionValue
  );
};

export const SettingsFormControls = {
  MultipleChoice,
};
